import { useNavigation } from "@refinedev/core";
import { CheckCircle2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { checkLabel } from "@/pages/tina/site-health/lib/check-labels";
import {
  brokenDays,
  describeFinding,
  isNewToday,
  plural,
  sampleEventIds,
} from "@/pages/tina/site-health/lib/describe-finding";
import type { SiteHealthFindingRow, SiteHealthSeverity } from "@/types/tina";

export function SeverityBadge({ severity }: { severity: SiteHealthSeverity }) {
  return (
    <Badge
      className={cn(
        "text-[10px] text-white",
        severity === "red" ? "bg-red-600" : "bg-amber-500",
      )}
    >
      {severity}
    </Badge>
  );
}

export interface FindingActions {
  onSnooze: () => void;
  onSolve: () => void;
  onDisable: () => void;
  onDiscoverTest: () => void;
  /** Disables Solved while its call is in flight, so a double click cannot fire it twice. */
  solveBusy?: boolean;
}

export const SOLVE_TOOLTIP =
  "Close this card. Tina waits for fresh data before it checks this site again; if the site is still broken then, a new card appears.";

export function FindingActionsRow({
  onSnooze,
  onSolve,
  onDisable,
  onDiscoverTest,
  solveBusy,
}: FindingActions) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={onSnooze}>
        Snooze
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onSolve}
        disabled={solveBusy}
        title={SOLVE_TOOLTIP}
      >
        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
        Solved
      </Button>
      <Button size="sm" variant="outline" onClick={onDisable}>
        Disable
      </Button>
      <Button size="sm" variant="ghost" onClick={onDiscoverTest}>
        Discover test
      </Button>
    </div>
  );
}

const athensDateTime = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Athens",
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * "Snoozed until … — note" line for a snoozed card, omitting the note when the snooze had
 * none. A second snooze replaces the first, so this always reflects the CURRENT snooze,
 * not a history of them. No "by": `snoozed_by` is a raw Firebase uid, meaningless here
 * (tina ruling R24).
 */
function snoozedLine(finding: SiteHealthFindingRow): string | null {
  if (!finding.snoozed) return null;
  const until = finding.snoozed_until
    ? `Snoozed until ${athensDateTime.format(new Date(finding.snoozed_until))}`
    : "Snoozed";
  return finding.snoozed_note ? `${until} — ${finding.snoozed_note}` : until;
}

/** `cluttered`: what was detected — the repeated lines and the pages that show it. No fix (tina spec D1). */
function ClutterDetails({ finding }: { finding: SiteHealthFindingRow }) {
  const lines = finding.evidence.template_lines ?? [];
  const urls = finding.evidence.sample_urls ?? [];
  if (finding.check_name !== "cluttered" || (lines.length === 0 && urls.length === 0)) return null;
  return (
    <div className="flex flex-col gap-1 text-[11px] text-muted-foreground">
      {lines.length > 0 && (
        <div>
          <span className="font-medium">Repeated lines:</span>
          <ul className="ml-4 list-disc">
            {lines.map((line) => (
              <li key={line} className="font-mono">
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="font-medium">Example pages:</span>
          {urls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="font-mono hover:underline">
              {url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 60)}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function FindingDetails({ finding, now }: { finding: SiteHealthFindingRow; now: Date }) {
  const { editUrl, showUrl } = useNavigation();
  const samples = sampleEventIds(finding);
  const runId = finding.evidence.last_run_id;
  const snoozedText = snoozedLine(finding);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <a
          // `finding.site` can contain `/` (`allevents/ro/cluj`): `editUrl` composes the
          // route by plain string interpolation with no encoding of its own, so an
          // unencoded slashed name splits into extra path segments and 404s on the `*`
          // route. React Router's own matcher decodes `%2F` back to `/` within the single
          // `:id` segment before refine's `useParsed().id` runs its own (then no-op)
          // `decodeURIComponent`, so this round-trips correctly to `sites/edit.tsx`.
          href={editUrl("tina-sites", encodeURIComponent(finding.site))}
          className="font-mono text-sm font-medium hover:underline"
        >
          {finding.site}
        </a>
        <span className="text-xs text-muted-foreground">
          {[finding.city, finding.x_strategy].filter(Boolean).join(" · ")}
        </span>
        <SeverityBadge severity={finding.severity} />
        <span className="text-xs font-medium">{checkLabel(finding.check_name)}</span>
        {isNewToday(finding, now) && (
          <Badge variant="outline" className="text-[10px]">
            new
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {`broken for ${plural(brokenDays(finding, now), "day")}`}
        </span>
      </div>
      <p className="text-sm">{describeFinding(finding)}</p>
      {snoozedText && <p className="text-xs text-muted-foreground">{snoozedText}</p>}
      <ClutterDetails finding={finding} />
      {(samples.length > 0 || runId) && (
        <div className="flex flex-wrap gap-2 text-[11px]">
          {samples.map((id) => (
            <a
              key={id}
              href={showUrl("tina-items", id)}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-muted-foreground hover:underline"
            >
              {id.slice(0, 8)}
            </a>
          ))}
          {runId && (
            <a
              href={showUrl("tina-runs", runId)}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              last run
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function FindingCard({
  finding,
  now,
  ...actions
}: { finding: SiteHealthFindingRow; now: Date } & FindingActions) {
  return (
    <Card className={cn(finding.severity === "red" ? "border-red-500/40" : "border-amber-500/40")}>
      <CardContent className="flex flex-col gap-3 p-4">
        <FindingDetails finding={finding} now={now} />
        <FindingActionsRow {...actions} />
      </CardContent>
    </Card>
  );
}
